/**
 * The text of a rejected API call, always a string.
 *
 * FastAPI answers most refusals with `detail: "a sentence"`, but a request body
 * that fails validation (HTTP 422) answers `detail: [{type, loc, msg, input, ctx}]`.
 * A form that stores that in state and renders it hands React an object, and React
 * answers with error #31 and the whole app falls to its error screen. Typing a
 * malformed email into the sign-in form was enough to do it.
 *
 * - a string is returned as it came;
 * - a list of validation errors becomes one sentence naming each field
 *   ("Email: value is not a valid email address"), using `labels` for the names;
 * - anything else returns `fallback`.
 */
export function apiErrorText(err: unknown, fallback: string, labels: Record<string, string> = {}): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null | undefined)?.response?.data?.detail
  if (typeof detail === 'string') return detail.trim() || fallback
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        const entry = (item ?? {}) as { loc?: unknown; msg?: unknown }
        if (typeof entry.msg !== 'string' || !entry.msg.trim()) return null
        const loc = Array.isArray(entry.loc) ? entry.loc : []
        const field = String(loc[loc.length - 1] ?? '')
        const name = field && field !== 'body' ? labels[field] ?? field.replace(/_/g, ' ') : ''
        const label = name ? `${name.charAt(0).toUpperCase()}${name.slice(1)}: ` : ''
        return `${label}${entry.msg.trim()}`
      })
      .filter((part): part is string => part !== null)
    if (parts.length) return parts.join('. ')
  }
  return fallback
}
